import { useCallback, useMemo, useRef } from 'react';

import { PERM } from '#/config/permissions';
import { ROUTES } from '#/config/routes';
import useSideDetail from '#/hooks/useSideDetail';
import SideMsg from '#/modules/commonComponents/SideMsg';
import ListAddButton from '#/modules/commonComponents/table/ListAddButton';
import ListTablePanel from '#/modules/commonComponents/table/ListTablePanel';
import { buildFilterStatusFieldsFromColumns } from '#/modules/commonComponents/table/buildFilterStatusFields';
import { DEFAULT_LIST_SORT_FIELD_LABELS } from '#/modules/commonComponents/table/listFilterKeys';
import { useCan } from '#/modules/infra/auth/useCan';
import PageShell from '#/modules/infra/layout/PageShell';
import SideMsgLayout from '#/modules/infra/layout/SideMsgLayout';
import { buildManufacturerColumns } from '#/modules/imsMgmt/settings/manufacturer/data/manufacturerColumns';
import { manufacturerSideMsgBlocks } from '#/modules/imsMgmt/settings/manufacturer/data/manufacturerSideMsgConfig';
import { getManufacturerApiErrorMessage } from '#/modules/imsMgmt/settings/manufacturer/helper/apiError';
import useManufacturerList from '#/modules/imsMgmt/settings/manufacturer/hooks/useManufacturerList';

const ManufacturerListPage = () => {
  const listRef = useRef(null);
  const { displayData, setSelectedData } = useSideDetail();
  const canCreate = useCan(PERM.MTR_CREATE);
  const canUpdate = useCan(PERM.MTR_UPDATE);

  const {
    rows,
    pagination,
    isLoading,
    isError,
    error,
    refetch,
    listQuery,
    setPage,
    setPageSize,
    setKeyword,
    setFilter,
    removeSortAt,
    resetListQuery,
  } = useManufacturerList();

  const columns = useMemo(
    () => buildManufacturerColumns({
      filters: listQuery.filters || {},
      onFilterChange: setFilter,
      canShowEdit: () => canUpdate,
    }),
    [listQuery.filters, setFilter, canUpdate],
  );

  const filterStatusFields = useMemo(
    () => buildFilterStatusFieldsFromColumns(columns),
    [columns],
  );

  const handleRowClick = useCallback((row, isSelected) => {
    if (isSelected) {
      setSelectedData(row.original);
      return;
    }

    setSelectedData(null);
  }, [setSelectedData]);

  const headerAction = canCreate ? (
    <ListAddButton to={ROUTES.manufacturerCreate} />
  ) : null;

  return (
    <PageShell variant="listWithSide">
      <div className="layout-shell__body flex min-h-0 flex-col">
        <h1 className="page_title shrink-0">
          <span className="border-l-5 border-primary-400 pl-2.5">製造商</span>
        </h1>
        <SideMsgLayout>
          <div className="table-panel min-h-0 xl:h-full">
            <ListTablePanel
              ref={listRef}
              tableKey="ims.manufacturer.list"
              columns={columns}
              rows={rows}
              pagination={pagination}
              isLoading={isLoading}
              isError={isError}
              errorMessage={isError ? getManufacturerApiErrorMessage(error, '載入列表失敗') : undefined}
              onRetry={isError ? () => { refetch(); } : undefined}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              onRowClick={handleRowClick}
              onSearch={setKeyword}
              onReset={resetListQuery}
              initialKeyword={listQuery.keyword}
              searchPlaceholder="搜尋代號或名稱"
              headerAction={headerAction}
              listQuery={listQuery}
              filterStatusFields={filterStatusFields}
              sortFieldLabels={DEFAULT_LIST_SORT_FIELD_LABELS}
              onFilterChange={setFilter}
              onKeywordClear={() => setKeyword('')}
              onSortRemove={removeSortAt}
            />
          </div>

          <SideMsg
            title="製造商"
            titleValue="name"
            displayData={displayData}
            blocks={manufacturerSideMsgBlocks}
          />
        </SideMsgLayout>
      </div>
    </PageShell>
  );
};

export default ManufacturerListPage;
